<script lang="ts" setup>
// ===== 1. IMPORTS =====
import { OnClickOutside } from '@vueuse/components'
import { onKeyStroke } from '@vueuse/core'
import {
  type MaybeRefOrGetter,
  computed,
  onBeforeUnmount,
  onMounted,
  reactive,
  toValue,
  useTemplateRef,
  watch
} from 'vue'
import Loading from '@/components/library/Loading.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { useBodyScrollLock } from '@/composables/useBodyScrollLock'

// ===== 2. TYPE DEFINITIONS =====
type ModalText = MaybeRefOrGetter<string>
type ModalToggle = MaybeRefOrGetter<boolean>

interface Props {
  headerText?: string
  contentText?: string
  footerButtonLabel?: ModalText
  footerButtonLoading?: ModalToggle
  closeOnOutsideClick?: boolean
  closeOnConfirm?: boolean
  closeOnLoadEnd?: boolean
  showHeader?: boolean
  showBody?: boolean
  showFooter?: boolean
  showPrimaryCTA?: ModalToggle
  showCloseButton?: boolean
  blurBackground?: boolean
  showBorderHeader?: boolean
  addContentPadding?: boolean
  footerStyle?: string
  showSecondaryCTA?: ModalToggle
  secondaryCTALabel?: ModalText
  secondaryButtonLoading?: ModalToggle
  showTertiaryCTA?: ModalToggle
  tertiaryCTALabel?: ModalText
  tertiaryButtonLoading?: ModalToggle
  extendMobile?: boolean
  innerWidth?: string
  dialogClass?: string
  transparentBackdrop?: boolean
  position?: 'center' | 'bottom-right'
}

interface Emits {
  close: []
  confirm: []
  secondary: []
  tertiary: []
  cancel: []
}

// ===== 3. PROPS =====
const {
  headerText = 'Placeholder',
  contentText = 'Placeholder',
  footerButtonLabel = 'Placeholder',
  footerButtonLoading = false,
  closeOnOutsideClick = true,
  closeOnConfirm = true,
  closeOnLoadEnd = false,
  showHeader = true,
  showBody = true,
  showFooter = true,
  showPrimaryCTA = true,
  showCloseButton = true,
  blurBackground = false,
  showBorderHeader = true,
  addContentPadding = true,
  footerStyle = 'gap-4',
  showSecondaryCTA = false,
  secondaryCTALabel = 'Placeholder',
  secondaryButtonLoading = false,
  showTertiaryCTA = false,
  tertiaryCTALabel = 'Placeholder',
  tertiaryButtonLoading = false,
  extendMobile = false,
  innerWidth = '640px',
  dialogClass = '',
  transparentBackdrop = false,
  position = 'center'
} = defineProps<Props>()

// ===== 4. EMITS =====
const emit = defineEmits<Emits>()

// ===== 5. COMPOSABLES & EXTERNAL DEPENDENCIES =====
const { setIsLocked } = useBodyScrollLock()

const dialogRoot = useTemplateRef<HTMLDialogElement>('dialog-root')

// ===== 6. REACTIVE DATA =====
const state = reactive({ modalActive: true, loaded: false })

// ===== 7. COMPUTED PROPERTIES =====
const resolvedFooterButtonLoading = computed(
  () => toValue(footerButtonLoading) ?? false
)
const resolvedSecondaryButtonLoading = computed(
  () => toValue(secondaryButtonLoading) ?? false
)
const resolvedTertiaryButtonLoading = computed(
  () => toValue(tertiaryButtonLoading) ?? false
)
const resolvedShowPrimaryCTA = computed(() => toValue(showPrimaryCTA) ?? true)
const resolvedShowSecondaryCTA = computed(
  () => toValue(showSecondaryCTA) ?? false
)
const resolvedShowTertiaryCTA = computed(
  () => toValue(showTertiaryCTA) ?? false
)
const resolvedFooterButtonLabel = computed(() => toValue(footerButtonLabel))
const resolvedSecondaryCTALabel = computed(() => toValue(secondaryCTALabel))
const resolvedTertiaryCTALabel = computed(() => toValue(tertiaryCTALabel))

// ===== 8. WATCHERS =====
watch(
  [
    resolvedFooterButtonLoading,
    resolvedSecondaryButtonLoading,
    resolvedTertiaryButtonLoading
  ],
  ([primaryLoading, secondaryLoading, tertiaryLoading]) => {
    if (
      closeOnLoadEnd &&
      (primaryLoading || secondaryLoading || tertiaryLoading)
    ) {
      state.loaded = true
    } else if (
      closeOnLoadEnd &&
      state.loaded &&
      !(primaryLoading || secondaryLoading || tertiaryLoading)
    ) {
      close()
    }
  }
)

// ===== 9. LIFECYCLE HOOKS =====
onMounted(() => {
  setIsLocked(true)

  // The dialog has `autofocus`, so show() focuses the dialog itself
  // directly instead of auto-focusing the close button first.
  dialogRoot.value?.show()
})

onBeforeUnmount(() => setIsLocked(false))

// ===== 10. METHODS =====
function handleClickOutside(): void {
  if (closeOnOutsideClick) {
    close()
  }
}

onKeyStroke('Escape', (e: KeyboardEvent) => {
  e.preventDefault()
  e.stopImmediatePropagation()

  if (closeOnOutsideClick) {
    close()
  }
})

function cancel(): void {
  emit('cancel')
  close()
}

function close(): void {
  dialogRoot.value?.close()

  state.modalActive = false
  emit('close')
}

function confirm(): void {
  emit('confirm')
  if (closeOnConfirm) {
    close()
  }
}

const anyLoading = computed(
  () =>
    resolvedFooterButtonLoading.value ||
    resolvedSecondaryButtonLoading.value ||
    resolvedTertiaryButtonLoading.value
)
</script>

<template>
  <Teleport to="body">
    <slot name="outer-modal-replacement">
      <dialog
        ref="dialog-root"
        class="dialog-root"
        :class="[
          dialogClass,
          {
            blur: blurBackground,
            'extended-modal': extendMobile,
            'position-bottom-right': position === 'bottom-right',
            'transparent-backdrop': transparentBackdrop
          }
        ]"
        tabindex="-1"
        autofocus
      >
        <OnClickOutside class="display-contents" @trigger="handleClickOutside">
          <!-- Slot to replace whole modal with a component -->
          <slot name="inner-modal-replacement">
            <!-- Close Button -->
            <button
              v-if="showCloseButton"
              class="btn circle dialog-close"
              @click="cancel()"
            >
              <FontAwesomeIcon
                icon="fa-light fa-xmark"
                class="dialog-close-icon"
              />
              <span class="sr-only">close</span>
            </button>

            <!-- Title -->
            <div
              v-if="showHeader"
              class="dialog-header stack"
              :class="{ 'remove-border': !showBorderHeader }"
            >
              <slot name="header">
                <h2 class="font-size-2 header-default">
                  {{ headerText }}
                </h2>
              </slot>
            </div>

            <!-- Content -->
            <div
              v-if="showBody"
              class="dialog-content"
              :class="{ 'dialog-body': addContentPadding }"
            >
              <slot name="content">
                {{ contentText }}
              </slot>
            </div>

            <!-- Footer -->
            <footer
              v-if="showFooter"
              class="dialog-footer"
              :class="footerStyle"
            >
              <slot name="footer">
                <button
                  v-if="resolvedShowPrimaryCTA"
                  type="button"
                  class="btn primary"
                  :disabled="anyLoading"
                  @click="confirm()"
                >
                  <Loading :loading="resolvedFooterButtonLoading" />
                  <strong>{{ resolvedFooterButtonLabel }}</strong>
                </button>
                <button
                  v-if="resolvedShowSecondaryCTA"
                  type="button"
                  class="btn secondary"
                  :disabled="anyLoading"
                  @click="emit('secondary')"
                >
                  <Loading :loading="resolvedSecondaryButtonLoading" />
                  {{ resolvedSecondaryCTALabel }}
                </button>
                <button
                  v-if="resolvedShowTertiaryCTA"
                  type="button"
                  class="btn tertiary brand"
                  :disabled="anyLoading"
                  @click="emit('tertiary')"
                >
                  <Loading :loading="resolvedTertiaryButtonLoading" />
                  {{ resolvedTertiaryCTALabel }}
                </button>
              </slot>
            </footer>
          </slot>
        </OnClickOutside>
      </dialog>
    </slot>
  </Teleport>
</template>

<style scoped>
.dialog-root {
  transform: scale(0.8);
  outline: none;

  /* This container is tabindex="-1" and never keyboard-reachable, so it
     never needs a visible focus ring, including WebKit's :focus-visible
     misfire on dialogs that auto-focus an icon-bearing button (see
     .dialog-close-icon below). */
  &:focus,
  &:focus-visible {
    outline: none !important;
  }

  transition:
    transform 0.3s cubic-bezier(0.52, 0.02, 0.19, 1.02),
    overlay 0.3s cubic-bezier(0.52, 0.02, 0.19, 1.02) allow-discrete,
    display 0.3s cubic-bezier(0.52, 0.02, 0.19, 1.02) allow-discrete;

  position: fixed;
  box-shadow: var(--shadow-5);
  max-height: 100dvh;

  width: 100%;
  max-width: v-bind(innerWidth);
  height: fit-content;

  display: flex;
  flex-direction: column;

  margin-block: auto 0;

  padding: 0;
  border: none;
  border-start-start-radius: 0.5rem;
  border-start-end-radius: 0.5rem;
  overflow: visible;

  z-index: 9998; /* vue3-toastify's default is 9999 */

  @media (width >= 48em) {
    margin-block: auto;

    border-radius: 0.5rem;
  }

  &::before {
    content: '';
    position: absolute;
    inset: -200vh -200vw;
    z-index: -2;

    background: rgb(0 0 0 / 0.55);
    opacity: 0;

    transition: opacity 0.3s cubic-bezier(0.52, 0.02, 0.19, 1.02);

    backdrop-filter: blur(4px);
  }

  &.blur::before {
    -webkit-backdrop-filter: blur(10px);
    backdrop-filter: blur(10px);
  }

  &.transparent-backdrop::before {
    background: transparent;
    pointer-events: none;
    backdrop-filter: none;
  }

  &.position-bottom-right {
    margin-block: auto 0;
    margin-inline: auto var(--size-1);
    inset-inline-end: 0;

    @media (width >= 48em) {
      margin-block: auto var(--size-1);
      max-width: 450px;
    }
  }

  &.position-bottom-right::before {
    background: transparent;
    pointer-events: none;
    backdrop-filter: none;
  }

  &.dialog-mobile-full-height {
    height: 100dvh;

    @media (width >= 48em) {
      height: fit-content;
    }
  }

  &.extended-modal {
    height: fit-content;
    max-height: 90vh;
    max-height: 90dvb;

    @media (width >= 48em) {
      max-height: 100dvh;
    }
  }

  @media (width >= 48em) {
    &.dialog-desktop-bottom-right {
      margin-block: auto var(--size-1);
      margin-inline: auto var(--size-1);
      inset-block-end: 0;
      inset-inline-end: 0;
      max-width: 450px;

      &::before {
        background: transparent;
        pointer-events: none;
        backdrop-filter: none;
      }
    }
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    z-index: -1;

    background: var(--surface-1);
    border-radius: inherit;
  }

  &[open] {
    transform: scale(1);
  }

  &[open]::before {
    opacity: 1;
  }
}

.dialog-header {
  padding: var(--size-1) var(--size-1);
  white-space: wrap;
  text-align: start;

  align-items: flex-start;

  @media (width >= 48em) {
    padding: var(--size-1) var(--size-3);
  }
}

.remove-border {
  border: none;
}

.dialog-footer {
  padding-top: var(--size-0);

  @media (min-width: 48em) {
    padding-top: 0;
  }

  strong {
    --base-weight: 500;
  }
}
.dialog-close {
  position: absolute;
  top: 0;
  right: 0;
  inset-block-start: 0;
  inset-inline-end: 0;

  z-index: 1;

  &:focus:not(:focus-visible) {
    outline: none;
  }

  svg {
    width: 1rem;
  }
}

.dialog-close-icon {
  width: 20px;
  height: 20px;

  /* Keeps focus/click resolving to the button, not this SVG, avoiding a
     WebKit bug where an auto-focused button wrapping an icon paints a
     spurious :focus-visible ring (bugs.webkit.org/show_bug.cgi?id=247416). */
  pointer-events: none;
}

.header-default {
  font-variation-settings: 'wght' 500;
  text-wrap-style: auto;

  padding-inline-end: var(--size-2);
}

.dialog-content {
  flex-shrink: 1;
  flex-grow: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.extended-modal .dialog-content {
  flex-grow: 1;
  max-block-size: none;
  overflow-y: auto;
  overscroll-behavior: contain;

  @media (width >= 48em) {
    flex-grow: 0;
    max-block-size: 90vh;
    max-block-size: 90dvb;
    overflow-y: visible;
  }
}

.btn.tertiary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
